// src/features/media/components/MergeMetadataForm.tsx

import { Stack, TextInput, Textarea, Switch, Select, Divider, SimpleGrid } from '@mantine/core';
import { DateTimePicker } from '@mantine/dates';
import { DateTime } from 'luxon';
import { VIDEO_CONSTRAINTS } from '@/lib/constants/media';
import { TagInput } from '@/shared/components/TagInput';
import type { MergeFormState } from '../MergeUtils';

interface VodOption {
  value: string;
  label: string;
}

interface MergeMetadataFormProps {
  formState: MergeFormState;
  onFieldChange: <K extends keyof MergeFormState>(field: K, value: MergeFormState[K]) => void;
  vodOptions: VodOption[];
  vodsLoading: boolean;
  allTags: string[];
}

function timestampToDateStr(ts: number | null): string | null {
  if (typeof ts !== 'number' || !isFinite(ts)) return null;
  return DateTime.fromSeconds(ts).toFormat('yyyy-MM-dd HH:mm:ss');
}

function dateStrToTimestamp(dateStr: string): number {
  return Math.floor(
    DateTime.fromFormat(dateStr, 'yyyy-MM-dd HH:mm:ss').toUTC().toSeconds()
  );
}

export function MergeMetadataForm({
  formState,
  onFieldChange,
  vodOptions,
  vodsLoading,
  allTags,
}: MergeMetadataFormProps) {
  const isActive = formState.mediaStateType === 'active';
  const titleLength = formState.title.length;
  const descShortLength = formState.descShort.length;
  const titleError = !formState.title.trim() ? 'Title is required' : undefined;

  return (
    <Stack gap="md">
      <TextInput
        label="Title"
        value={formState.title}
        onChange={(e) => onFieldChange('title', e.currentTarget.value)}
        maxLength={VIDEO_CONSTRAINTS.TITLE_MAX}
        description={`${titleLength}/${VIDEO_CONSTRAINTS.TITLE_MAX}`}
        error={titleError}
        required
      />

      <Textarea
        label="Short Description"
        value={formState.descShort}
        onChange={(e) => onFieldChange('descShort', e.currentTarget.value)}
        maxLength={VIDEO_CONSTRAINTS.DESC_SHORT_MAX}
        description={`${descShortLength}/${VIDEO_CONSTRAINTS.DESC_SHORT_MAX}`}
        rows={2}
      />

      <TagInput
        value={formState.tags}
        onChange={(tags) => onFieldChange('tags', tags)}
        allTags={allTags}
      />

      <Divider />

      <SimpleGrid cols={2}>
        <Switch
          label={isActive ? 'Active \u2014 publicly available' : 'Inactive \u2014 hidden from viewers'}
          checked={isActive}
          onChange={(event) => {
            onFieldChange('mediaStateType', event.currentTarget.checked ? 'active' : 'inactive');
          }}
          color={isActive ? 'green' : 'gray'}
          size="md"
        />

        <DateTimePicker
          label="Published"
          value={timestampToDateStr(formState.published)}
          onChange={(val) => {
            if (val) {
              onFieldChange('published', dateStrToTimestamp(val));
            }
          }}
          valueFormat="MMM D, YYYY h:mm A"
          timePickerProps={{ format: '12h' }}
          clearable={false}
        />
      </SimpleGrid>

      <Divider />

      <Select
        label="Output Quality"
        data={vodOptions}
        value={formState.transcodeVodId?.toString() ?? null}
        onChange={(val) => onFieldChange('transcodeVodId', val ? parseInt(val, 10) : null)}
        disabled={vodsLoading}
        placeholder={vodsLoading ? 'Loading...' : 'Select quality'}
      />
    </Stack>
  );
}
